/**
 * A rule written in the rule language, as a Crown expression.
 *
 * The rule language (`field('A').gte('B')`, `.requiredIf(...)`, `.refine(...)`)
 * is older than the expression language and has an evaluator of its own in
 * `unified-program.ts`. This writes each rule as the expression that means it,
 * so a rule can be converted and edited as an expression, run by the engine
 * that runs everything else, and learnt from as one language rather than two.
 *
 * The expression answers `true` when the rule passes and `false` or the
 * rule's message when it does not, in the language's own terms - which is
 * how every rule-language rule is now run. They differ from the evaluator the
 * rule language used to have in three deliberate places:
 *
 * - **a missing value is unknown, and an unknown passes.** A comparison with
 *   a field that is absent, null or empty text is unknown in the language,
 *   and a check fails only what is definitely false, as a SQL CHECK does.
 *   The old evaluator compared an explicit null as JavaScript does, so
 *   `null >= 5` failed and `null <= 5` passed. `eq`/`neq` compare for
 *   certain, so a blank on either side is passed over explicitly.
 * - **a quoted name compared by order is a field.** `gte('B')` reads field `B`.
 *   The old evaluator read it as the text `"B"` when the record had no such
 *   field.
 * - **values compare as the language compares them.** Dates are dates and
 *   numbers are decimals, rather than JavaScript's coercions.
 */

import type { DslInstruction } from './ast.js';

/** A string as a Crown string literal. */
function text(value: string): string {
  return JSON.stringify(value);
}

/** A field of the record, dotted when its name allows it. */
function field(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? `root.${name}` : `root[${text(name)}]`;
}

/** Whether a field holds nothing: absent, null or empty text - for `eq`/`neq`. */
function blank(expr: string): string {
  return `(${expr} == null || ${expr} == "")`;
}

/**
 * The other side of a comparison, as its token was written.
 *
 * `asField` is where a quoted name means a field: a comparison by order, a
 * range, a condition. For `eq` and `neq` a quoted word is a value.
 */
function argument(value: string, type: string | undefined, asField: boolean): string {
  if (type === 'number') return String(Number(value.replace(/_/g, '')));
  if (type === 'string') return asField ? field(value) : text(value);
  const lowered = value.toLowerCase();
  if (lowered === 'true' || lowered === 'false' || lowered === 'null') return lowered;
  if (type === undefined && /^-?\d/.test(value) && !Number.isNaN(Number(value.replace(/_/g, '')))) {
    return String(Number(value.replace(/_/g, '')));
  }
  return field(value);
}

/** What a failing check answers: the rule's message, or `false`. */
function failure(message: string | undefined, fallback?: string): string {
  const said = message ?? fallback;
  return said === undefined ? 'false' : text(said);
}

/**
 * One instruction as `[verdict, what it answers when it fails]`. A verdict is
 * `true`, `false`, or null for unknown; only `false` fails.
 */
function check(instr: DslInstruction): [string, string] {
  switch (instr.kind) {
    case 'cmp': {
      const f = field(instr.field);
      const o = argument(instr.other, instr.otherType, instr.op !== 'eq' && instr.op !== 'neq');
      const holds = {
        gte: `${f} >= ${o}`,
        lte: `${f} <= ${o}`,
        gt: `${f} > ${o}`,
        lt: `${f} < ${o}`,
        gteDateOnly: `gteDateOnly(${f}, ${o})`,
        lteDateOnly: `lteDateOnly(${f}, ${o})`,
        eq: `${blank(f)} || ${blank(o)} || ${f} == ${o}`,
        neq: `${blank(f)} || ${blank(o)} || ${f} != ${o}`,
      }[instr.op];
      return [holds, 'false'];
    }
    case 'within': {
      const f = field(instr.field);
      const lo = argument(instr.minField, instr.minFieldType, true);
      const hi = argument(instr.maxField, instr.maxFieldType, true);
      return [`${f} >= ${lo} && ${f} <= ${hi}`, 'false'];
    }
    case 'withinHours':
    case 'withinMinutes': {
      const f = field(instr.field);
      const o = argument(instr.other, instr.otherType, true);
      const limit = instr.kind === 'withinHours' ? instr.hours : instr.minutes;
      return [`${instr.kind}(${f}, ${o}, ${limit})`, 'false'];
    }
    case 'requiredIf': {
      const o = argument(instr.other, instr.otherType, true);
      return [`!(${o}) || !${blank(field(instr.field))}`, 'false'];
    }
    case 'requiredIfValue': {
      const o = argument(instr.other, instr.otherType, true);
      const v = argument(instr.value, instr.valueType, false);
      return [
        `${o} != ${v} || !${blank(field(instr.field))}`,
        failure(instr.opts?.message, `${instr.field} is required when ${instr.other} is ${instr.value}`),
      ];
    }
    case 'notFutureDate': {
      const f = field(instr.field);
      return [
        `toDate(${f}) <= now()`,
        failure(instr.opts?.message, `${instr.field} cannot be in the future`),
      ];
    }
    case 'datesNotFuture': {
      // One check per field, so the message names the one that failed.
      throw new Error('datesNotFuture is expanded before it reaches here');
    }
    // A refinement passes when its expression is truthy or unknown.
    case 'refine':
      return [
        `((value) => ((verdict) => verdict == null || !!verdict)(${instr.expr}))(${field(instr.field)})`,
        failure(instr.opts?.message, `Validation failed for ${instr.field}`),
      ];
    case 'objectRefine':
      return [
        `((value) => ((verdict) => verdict == null || !!verdict)(${instr.expr}))(root)`,
        failure(instr.opts?.message, 'Object validation failed'),
      ];
  }
}

/** `datesNotFuture` as one `notFutureDate` per field, keeping its message. */
function expand(instructions: DslInstruction[]): DslInstruction[] {
  return instructions.flatMap((instr): DslInstruction[] =>
    instr.kind === 'datesNotFuture'
      ? instr.fields.map((f) => ({
          kind: 'notFutureDate',
          field: f,
          opts: instr.opts?.message ? { message: instr.opts.message } : undefined,
        }))
      : [instr],
  );
}

/**
 * The expression a rule-language rule means: `true` when no instruction
 * definitely fails, otherwise what the first failing one answers.
 */
export function dslToExpression(instructions: DslInstruction[]): string {
  const checks = expand(instructions).map(check);
  if (checks.length === 0) return 'true';
  return checks.reduceRight<string>(
    (rest, [verdict, otherwise]) => `(${verdict}) === false ? ${otherwise} : (${rest})`,
    'true',
  );
}
